/**
 * Lightweight hook for Wealthica2 connection status
 * Provides connection status without attempting token access or data fetching
 * Supports both singleton and multi-institution stores via feature flags
 */
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { WealthicaStorage } from '../utils/storage';
import { logger } from '@/services/LoggingService';

// Debouncing utilities to prevent log spam
let lastStatusCheck = 0;
const STATUS_CHECK_DEBOUNCE_MS = 100; // Limit status checks to once per 100ms

/**
 * Hook to get minimal Wealthica2 connection status
 * Use this for UI display and conditional rendering without triggering data fetching
 * 
 * @returns Simple connection status information
 */
export function useWealthica2Status() {
  // Cutover: enforce multi-store only (flags removed)
  // Subscribe only to primitive slices to keep snapshots stable
  const isAnyConnected = useMultiWealthica2Store((state) => {
    for (const conn of state.connections.values()) {
      if (conn.isConnected) return true;
    }
    return false;
  });
  const hasAnyConnection = useMultiWealthica2Store((state) => state.connections.size > 0);
  const activeConnectionId = useMultiWealthica2Store((state) => state.activeConnectionId);

  const status: 'connected' | 'idle' = isAnyConnected ? 'connected' : 'idle';
  const isConnected = isAnyConnected;
  const institutionId = activeConnectionId || undefined;
  
  // Keep storage check but gate it to prevent limbo states during logout
  const storageConnectionData = isConnected ? WealthicaStorage.getConnectionData() : null;
  // Derive token availability from connection presence; server-side token acquisition is used by services
  const storageHasToken = isConnected ? WealthicaStorage.hasToken() : false;
  const hasToken = isConnected && (storageHasToken || hasAnyConnection);
  
  // Debounced logging to prevent spam
  const now = Date.now();
  if (now - lastStatusCheck > STATUS_CHECK_DEBOUNCE_MS) {
    lastStatusCheck = now;
    logger.debug(
      'useWealthica2Status', 
      'Status check using store state',
      { 
        storeMode: 'multi-store',
        storeStatus: status,
        storeConnected: isConnected,
        hasConnectionData: isConnected && hasAnyConnection,
        hasToken,
        institutionId
      }
    );
  }
  
  return {
    isWealthicaConnected: !!isConnected,
    isWealthicaLoading: false, // simplified: only connected/idle exposed in this hook during triage
    hasConnectionData: isConnected && hasAnyConnection,
    institutionId: isConnected ? (institutionId || storageConnectionData?.institutionId) : undefined,
    hasToken
  };
}
